<template>
    <section id="personalData" class="personal-data-card card mb-3">
        <form
            ref="personalInfoForm"
            v-if="candidate"
            class="card-body"
            v-click-outside="onClickOutside"
            @click="focusBlock"
        >
            <div class="card-heading">
                <h5 class="card-name">
                    {{ $t('lib_personal_info') }}
                </h5>
            </div>
            <hr />
            <div class="candidate-details">
                <div class="row">
                    <div class="form-group col-sm-4">
                        <label for="firstName" class="form-label">
                            {{ $t('lib_firstname') }}
                        </label>
                        <input
                            id="firstName"
                            disabled
                            type="text"
                            :value="candidate.usualFirstName"
                            class="form-control"
                            :class="{
                                'is-invalid': errorMessage(
                                    'usualFirstName',
                                    block.name
                                ),
                            }"
                        />
                        <div
                            v-if="errorMessage('usualFirstName', block.name)"
                            class="invalid-feedback"
                        >
                            {{ $t(errorMessage('usualFirstName', block.name)) }}
                        </div>
                    </div>
                    <div class="form-group col-sm-4">
                        <label for="lastName" class="form-label">
                            {{ $t('lib_lastname') }}
                        </label>
                        <input
                            id="lastName"
                            disabled
                            type="text"
                            :value="candidate.usualLastName"
                            class="form-control"
                            :class="{
                                'is-invalid': errorMessage(
                                    'usualLastName',
                                    block.name
                                ),
                            }"
                        />
                        <div
                            v-if="errorMessage('usualLastName', block.name)"
                            class="invalid-feedback"
                        >
                            {{ $t(errorMessage('usualLastName', block.name)) }}
                        </div>
                    </div>
                    <div class="form-group col-sm-4">
                        <label for="email" class="form-label">
                            {{ $t('lib_email') }}
                        </label>
                        <input
                            type="text"
                            :value="candidate.email"
                            class="form-control"
                            :class="{
                                'is-invalid': errorMessage('email', block.name),
                            }"
                            id="email"
                            disabled
                        />
                        <div
                            v-if="errorMessage('email', block.name)"
                            class="invalid-feedback"
                        >
                            {{ $t(errorMessage('email', block.name)) }}
                        </div>
                    </div>
                    <div
                        class="form-group col-sm-4"
                        :class="{
                            'required-group': isFieldRequired('birthDate'),
                        }"
                    >
                        <label for="birthDate" class="form-label">
                            {{ $t('lib_birth_date') }}
                        </label>
                        <Calendar
                            appendTo="self"
                            v-model="candidate.birthDate"
                            :minDate="minDateValue"
                            :maxDate="maxDateValue"
                            :showIcon="true"
                            :class="{
                                'is-invalid': errorMessage(
                                    'birthDate',
                                    block.name
                                ),
                            }"
                            id="birthDate"
                            ariaLabel="Birth date"
                        />
                        <div
                            v-if="errorMessage('birthDate', block.name)"
                            class="invalid-feedback"
                        >
                            {{ $t(errorMessage('birthDate', block.name)) }}
                        </div>
                    </div>
                    <div
                        class="form-group col-sm-4"
                        :class="{
                            'required-group': isFieldRequired('birthCity'),
                        }"
                    >
                        <label for="birthCity" class="form-label">
                            {{ $t('lib_birth_place') }}
                        </label>
                        <input
                            id="birthCity"
                            type="text"
                            v-model="candidate.birthCity"
                            class="form-control"
                            :class="{
                                'is-invalid': errorMessage(
                                    'birthCity',
                                    block.name
                                ),
                            }"
                        />
                        <div
                            v-if="errorMessage('birthCity', block.name)"
                            class="invalid-feedback"
                        >
                            {{ $t(errorMessage('birthCity', block.name)) }}
                        </div>
                    </div>
                    <div
                        class="form-group col-sm-4"
                        :class="{
                            'required-group': isFieldRequired(
                                'birthIsoCountryCode'
                            ),
                        }"
                    >
                        <label for="birthCountry" class="form-label">
                            {{ $t('lib_birth_country') }}
                        </label>
                        <Dropdown
                            appendTo="self"
                            inputId="birthCountry"
                            v-model="candidate.birthIsoCountryCode"
                            :class="{
                                'is-invalid': errorMessage(
                                    'birthIsoCountryCode',
                                    block.name
                                ),
                            }"
                            :filter="true"
                            :showClear="true"
                            :options="countries"
                            optionLabel="name"
                            optionValue="code"
                            :placeholder="$t('cand_choose')"
                        />
                        <div
                            v-if="
                                errorMessage('birthIsoCountryCode', block.name)
                            "
                            class="invalid-feedback"
                        >
                            {{
                                $t(
                                    errorMessage(
                                        'birthIsoCountryCode',
                                        block.name
                                    )
                                )
                            }}
                        </div>
                    </div>
                    <div
                        class="form-group col-sm-4"
                        :class="{
                            'required-group':
                                isFieldRequired('natIsoCountryCode'),
                        }"
                    >
                        <label for="nationality" class="form-label">
                            {{ $t('lib_nationality') }}
                        </label>
                        <Dropdown
                            appendTo="self"
                            inputId="nationality"
                            v-model="candidate.natIsoCountryCode"
                            :options="natIsoCountries"
                            optionLabel="name"
                            optionValue="code"
                            :filter="true"
                            :showClear="true"
                            :placeholder="$t('cand_choose')"
                            :class="{
                                'is-invalid': errorMessage(
                                    'natIsoCountryCode',
                                    block.name
                                ),
                            }"
                        >
                        </Dropdown>
                        <div
                            v-if="errorMessage('natIsoCountryCode', block.name)"
                            class="invalid-feedback"
                        >
                            {{
                                $t(
                                    errorMessage(
                                        'natIsoCountryCode',
                                        block.name
                                    )
                                )
                            }}
                        </div>
                    </div>
                    <div class="form-group col-sm-4">
                        <label for="secondnationality" class="form-label">
                            {{ $t('lib_nationality2') }}
                        </label>
                        <Dropdown
                            appendTo="self"
                            inputId="secondnationality"
                            v-model="candidate.nat2IsoCountryCode"
                            :options="nat2IsoCountries"
                            optionLabel="name"
                            optionValue="code"
                            :filter="true"
                            :placeholder="$t('cand_choose')"
                            :showClear="true"
                            :class="{
                                'is-invalid': errorMessage(
                                    'nat2IsoCountryCode',
                                    block.name
                                ),
                            }"
                        >
                        </Dropdown>
                        <div
                            v-if="
                                errorMessage('nat2IsoCountryCode', block.name)
                            "
                            class="invalid-feedback"
                        >
                            {{
                                $t(
                                    errorMessage(
                                        'nat2IsoCountryCode',
                                        block.name
                                    )
                                )
                            }}
                        </div>
                    </div>
                    <div
                        v-if="genders"
                        class="form-group col-sm-4"
                        :class="{
                            'required-group': isFieldRequired('gender'),
                        }"
                    >
                        <label for="gender" class="form-label">
                            {{ $t('lib_gender') }}
                        </label>
                        <Dropdown
                            appendTo="self"
                            inputId="gender"
                            v-model="candidate.gender"
                            :class="{
                                'is-invalid': errorMessage(
                                    'gender',
                                    block.name
                                ),
                            }"
                            :options="genders"
                            optionLabel="name"
                            optionValue="code"
                            :placeholder="$t('cand_choose')"
                        />
                        <div
                            v-if="errorMessage('gender', block.name)"
                            class="invalid-feedback"
                        >
                            {{ $t(errorMessage('gender', block.name)) }}
                        </div>
                    </div>
                    <div
                        v-if="candidate.gender === 'OTHER'"
                        class="form-group col-sm-4"
                    >
                        <label for="identifyAs" class="form-label">
                            {{ $t('lib_pref_pronouns') }}
                        </label>
                        <input
                            type="text"
                            v-model="candidate.identifyAs"
                            class="form-control"
                            :class="{
                                'is-invalid': errorMessage(
                                    'identifyAs',
                                    block.name
                                ),
                            }"
                            id="identifyAs"
                        />
                        <div
                            v-if="errorMessage('identifyAs', block.name)"
                            class="invalid-feedback"
                        >
                            {{ $t(errorMessage('identifyAs', block.name)) }}
                        </div>
                    </div>
                    <div 
                         class="form-group col-sm-4"
                         :class="{
                            'required-group': isFieldRequired('phoneNumber'),
                        }"
                         >
                        <label for="telephone" class="form-label">
                            {{ $t('lib_phone') }}
                        </label>
                        <InputMask
                                   mask="+9999999999?99999"
                                   :unmask="true"
                                   :autoClear="false"
                                   slotChar=""
                                   id="telephone"
                                   name="phone"
                                   autocomplete="tel"
                                   class="form-control"
                                   :class="{
                                'is-invalid': errorMessage(
                                    'phoneNumber',
                                    block.name
                                ),
                            }"
                                   v-model="candidate.phoneNumber"
                                   min="11"
                                   max="20"
                                   />
                        <small>
                            Eg: +32 99999999.
                        </small>
                        <div 
                             v-if="errorMessage('phoneNumber', block.name)"
                             class="invalid-feedback"
                             >
                            {{ $t(errorMessage('phoneNumber', block.name)) }}
                        </div>
                    </div>
                    <div class="form-group col-sm-4">
                        <label for="gsm" class="form-label">
                            {{ $t('lib_phone2') }}
                        </label>
                        <InputMask
                                   mask="+999999?999999999"
                                   :unmask="true"
                                   :autoClear="false"
                                   slotChar=""
                                   id="gsm"
                                   name="phone2"
                                   class="form-control"
                                   :class="{
                                'is-invalid': errorMessage(
                                    'gsmNumber',
                                    block.name
                                ),
                            }"
                                   v-model="candidate.gsmNumber"
                                   min="6"
                                   max="20"
                                   />
                        <small>
                            Eg: +32 99999999.
                        </small>
                        <div 
                             v-if="errorMessage('gsmNumber', block.name)"
                             class="invalid-feedback"
                             >
                            {{ $t(errorMessage('gsmNumber', block.name)) }}
                        </div>
                    </div>
                    <div class="form-group col-sm-4">
                        <label for="secondaryEmail" class="form-label">
                            {{ $t('lib_secondary_email') }}
                        </label>
                        <input
                            type="email"
                            v-model="candidate.secondaryEmail"
                            class="form-control"
                            :class="{
                                'is-invalid': errorMessage('secondaryEmail', block.name),
                            }"
                            id="secondaryEmail"
                        />
                        <div
                            v-if="errorMessage('secondaryEmail', block.name)"
                            class="invalid-feedback"
                        >
                            {{ $t(errorMessage('secondaryEmail', block.name)) }}
                        </div>
                    </div>                
                </div>
            </div>
            <div class="card-footer d-flex justify-content-end mt-3" v-if="formWasChanged">
                <div class="card-actions ">
                    <button
                        type="button"
                        class="card-action save-action btn btn-text me-2"
                        @click="validate()"
                        :disabled="appLoading"
                    >
                        <font-awesome-icon icon="far fa-save" class="fs-5 btn-icon"/>
                        <span class="btn-label">
                            {{ $t('btn_save') }}
                        </span>
                    </button>
                    <button
                        type="button"
                        class="card-action undo-changes-action btn btn-text"
                        @click="undoChanges()"
                        :disabled="appLoading"
                    >
                        <font-awesome-icon icon="fas fa-undo" class="btn-icon"/>
                        <span class="btn-label">
                            {{ $t('btn_cancel') }}
                        </span>
                    </button>
                </div>
            </div>
        </form>
    </section>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import Calendar from 'primevue/calendar';
import InputMask from 'primevue/inputmask';
import Dropdown from 'primevue/dropdown';
import _ from 'lodash';

export default {
    name: 'PersonalData',
    components: {
        Calendar,
        InputMask,
        Dropdown,
    },
    props: {
        block: {
            type: Object,
            required: true,
        },
        applicationId: {
            type: String,
            required: true,
        },
        applicationType: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            candidate: null,
            listenToClickOutside: false,
            formWasChanged: false,
        };
    },
    computed: {
        ...mapGetters([
            'genders',
            'countries',
            'errorMessage',
            'minDateValue',
            'maxDateValue',
            'isFieldRequired',
            'personalData',
            'appLoading',
        ]),
        natIsoCountries() {
            // Nationality should be different of Second Nationality;
            return this.countries.filter(
                (country) => country?.code !== this.candidate.nat2IsoCountryCode
            );
        },
        nat2IsoCountries() {
            // Second Nationality should be different of Nationality;
            return this.countries.filter(
                (country) => country?.code !== this.candidate.natIsoCountryCode
            );
        },
        // formWasChanged() {
        //     if(JSON.stringify(this.candidate) === JSON.stringify(this.personalData)) {
        //         this.clearBlockErrors(this.block.name);
        //     }
        //     return (
        //         JSON.stringify(this.candidate) !==
        //         JSON.stringify(this.personalData)
        //     );
        // },
    },
    methods: {
        ...mapActions([
            'validateBlock',
            'updateBlock',
            'clearBlockErrors',
            'focusOnBlock',
        ]),
        validate() {
            this.clearBlockErrors(this.block.name);
            this.validateBlock({
                blockData: { ...this.candidate },
                blockName: this.block.name,
            }).then(
                () => {
                    // success
                    this.updateBlock({
                        blockName: this.block.name,
                        application: {
                            applicationId: this.applicationId,
                            applicationType: this.applicationType,
                            applicationBlocks: {
                                [this.block.name]: { ...this.candidate },
                            },
                        },
                    }).then(
                        () => {
                            // success
                            this.listenToClickOutside = false;
                            this.$toast.add({
                                severity: 'success',
                                detail: this.$t('mess_save_success'),
                                life: 3000,
                            });
                            this.loadData();
                            this.$emit('checkErrorForOtherBlocks');
                        },
                        () => {
                            // error
                            this.listenToClickOutside = false;
                            this.$toast.add({
                                severity: 'error',
                                detail: this.$t('mess_save_failed'),
                                life: 3000,
                            });
                        }
                    );
                },
                () => {
                    // error
                    this.listenToClickOutside = false;
                    this.$toast.add({
                        severity: 'error',
                        detail: this.$t('mess_save_failed'),
                        life: 3000,
                    });
                }
            );
        },
        onClickOutside() {
            if (
                this.listenToClickOutside &&
                this.formWasChanged &&
                !this.appLoading
            ) {
                this.clearBlockErrors(this.block.name);
                this.validate();
            }
            this.focusOnBlock(null);
        },
        undoChanges() {
            this.loadData();
            this.clearBlockErrors(this.block.name);
        },
        loadData() {
            this.candidate = _.cloneDeep(this.personalData);
            this.candidate.birthDate = this.candidate.birthDate
                ? new Date(this.candidate.birthDate)
                : this.candidate.birthDate;
        },
        focusBlock() {
            this.listenToClickOutside = true;
            this.focusOnBlock({
                name: 'personalData',
                label: this.$t('lib_personal_info'),
            });
        },
    },
    mounted() {
        this.loadData();
    },
    watch: {
        // whenever personal data object changes, this function will run and this way we will now if the form data was changed so we can save changes;
        candidate: {
            handler(newData, oldData) {
                const storeData = {
                    ...this.personalData,
                    birthDate: this.personalData.birthDate
                        ? new Date(this.personalData.birthDate)
                        : this.personalData.birthDate,
                };
                this.formWasChanged =
                    JSON.stringify(newData) !== JSON.stringify(storeData);

                if (!this.formWasChanged && oldData) {
                    // checking for oldData to prevent clearing the errors on firstload;
                    this.clearBlockErrors(this.block.name);
                }
            },
            deep: true,
        },
    },
};
</script>
